// Утилиты для работы с Telegram WebApp
import toast from 'react-hot-toast';

// Проверяем, запущено ли приложение в Telegram
export const isTelegramWebApp = () => {
  // В режиме разработки разрешаем запуск без Telegram
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  return window.Telegram && window.Telegram.WebApp;
};

// Получаем тему Telegram
export const getTelegramTheme = () => {
  if (!isTelegramWebApp()) {
    return 'light'; // По умолчанию светлая тема
  }
  
  const webApp = window.Telegram?.WebApp;
  if (!webApp) {
    return 'light';
  }
  
  return webApp.colorScheme || 'light';
};

// Получаем параметры темы Telegram
export const getTelegramThemeParams = () => {
  if (!isTelegramWebApp()) {
    // Возвращаем дефолтные параметры для светлой темы
    return {
      bg_color: '#ffffff',
      text_color: '#000000',
      hint_color: '#999999',
      link_color: '#2481cc',
      button_color: '#2481cc',
      button_text_color: '#ffffff',
      secondary_bg_color: '#f1f1f1',
    };
  }
  
  const webApp = window.Telegram?.WebApp;
  if (!webApp) {
    return {
      bg_color: '#ffffff',
      text_color: '#000000',
      hint_color: '#999999',
      link_color: '#2481cc',
      button_color: '#2481cc',
      button_text_color: '#ffffff',
      secondary_bg_color: '#f1f1f1',
    };
  }
  
  return webApp.themeParams || {};
};

// Применяем тему Telegram к документу
export const applyTelegramTheme = () => {
  const theme = getTelegramTheme();
  const themeParams = getTelegramThemeParams();
  
  const root = document.documentElement;
  
  // Устанавливаем CSS переменные для темы
  root.style.setProperty('--tg-theme-bg-color', themeParams.bg_color || '#ffffff');
  root.style.setProperty('--tg-theme-text-color', themeParams.text_color || '#000000');
  root.style.setProperty('--tg-theme-hint-color', themeParams.hint_color || '#999999');
  root.style.setProperty('--tg-theme-link-color', themeParams.link_color || '#2481cc');
  root.style.setProperty('--tg-theme-button-color', themeParams.button_color || '#2481cc');
  root.style.setProperty('--tg-theme-button-text-color', themeParams.button_text_color || '#ffffff');
  root.style.setProperty('--tg-theme-secondary-bg-color', themeParams.secondary_bg_color || '#f1f1f1');
  
  // Добавляем класс темы к body
  document.body.className = `theme-${theme}`;
  
  // Устанавливаем цвет фона для body
  document.body.style.backgroundColor = themeParams.bg_color || '#ffffff';
  
  return { theme, themeParams };
};

// Устанавливаем слушатель событий для изменения темы
export const setupThemeListener = () => {
  if (!isTelegramWebApp()) {
    return;
  }
  
  const webApp = window.Telegram?.WebApp;
  if (!webApp) {
    return;
  }
  
  // Слушаем изменения темы
  webApp.onEvent('themeChanged', () => {
    console.log('Theme changed, applying new theme...');
    applyTelegramTheme();
  });
  
  // Слушаем изменения параметров темы
  webApp.onEvent('themeParamsChanged', () => {
    console.log('Theme params changed, applying new theme...');
    applyTelegramTheme();
  });
};

// Получаем данные из Telegram WebApp
export const getTelegramData = () => {
  if (!isTelegramWebApp()) {
    // Возвращаем моковые данные для разработки
    if (process.env.NODE_ENV === 'development') {
      return {
        user: {
          id: 123456789,
          first_name: 'Тестовый',
          last_name: 'Пользователь',
          username: 'test_user',
          language_code: 'ru'
        },
        chat: {
          id: -987654321,
          type: 'group',
          title: 'Тестовый магазин'
        },
        chatType: 'group',
        platform: 'web',
        colorScheme: 'light',
        themeParams: getTelegramThemeParams()
      };
    }
    return null;
  }
  
  const webApp = window.Telegram.WebApp;

  // Если initData пустая — приложение открыто не через бота (прямой URL и т.п.)
  // Возвращаем null чтобы показать ошибку без запроса на сервер
  if (!webApp.initData) {
    console.warn('Telegram WebApp initData is empty — not opened via bot button');
    return null;
  }

  // initDataUnsafe.user может быть undefined если открыто не через inline web_app кнопку
  // Пробуем распарсить user из initData строки как fallback
  let user = webApp.initDataUnsafe?.user;
  if (!user) {
    try {
      const params = new URLSearchParams(webApp.initData);
      const userStr = params.get('user');
      if (userStr) {
        user = JSON.parse(decodeURIComponent(userStr));
      }
    } catch (e) {
      console.warn('Failed to parse user from initData:', e);
    }
  }

  return {
    initData: webApp.initData,
    initDataUnsafe: webApp.initDataUnsafe,
    user: user,
    chat: webApp.initDataUnsafe?.chat,
    chatType: webApp.initDataUnsafe?.chat_type,
    startParam: webApp.initDataUnsafe?.start_param,
    canSendAfter: webApp.initDataUnsafe?.can_send_after,
    authDate: webApp.initDataUnsafe?.auth_date,
    hash: webApp.initDataUnsafe?.hash,
    queryId: webApp.initDataUnsafe?.query_id,
    receiver: webApp.initDataUnsafe?.receiver,
    chatInstance: webApp.initDataUnsafe?.chat_instance,
    isExpired: webApp.initDataUnsafe?.is_expired,
    isFromSwitch: webApp.initDataUnsafe?.is_from_switch,
    platform: webApp.platform,
    colorScheme: webApp.colorScheme,
    themeParams: webApp.themeParams,
    headerColor: webApp.headerColor,
    backgroundColor: webApp.backgroundColor,
    isClosingConfirmationEnabled: webApp.isClosingConfirmationEnabled,
    backButton: webApp.BackButton,
    mainButton: webApp.MainButton,
    hapticFeedback: webApp.HapticFeedback,
    isVersionAtLeast: webApp.isVersionAtLeast,
    setHeaderColor: webApp.setHeaderColor,
    setBackgroundColor: webApp.setBackgroundColor,
    enableClosingConfirmation: webApp.enableClosingConfirmation,
    disableClosingConfirmation: webApp.disableClosingConfirmation,
    expand: webApp.expand,
    close: webApp.close,
    ready: webApp.ready,
    showAlert: webApp.showAlert,
    showConfirm: webApp.showConfirm,
    showPopup: webApp.showPopup,
    showScanQrPopup: webApp.showScanQrPopup,
    showBackButton: webApp.showBackButton,
    hideBackButton: webApp.hideBackButton,
    showMainButton: webApp.showMainButton,
    hideMainButton: webApp.hideMainButton,
    setMainButtonText: webApp.setMainButtonText,
    setMainButtonParams: webApp.setMainButtonParams,
    onEvent: webApp.onEvent,
    offEvent: webApp.offEvent,
    sendData: webApp.sendData,
    switchInlineQuery: webApp.switchInlineQuery,
    openTelegramLink: webApp.openTelegramLink,
    openInvoice: webApp.openInvoice,
    readTextFromClipboard: webApp.readTextFromClipboard,
    requestWriteAccess: webApp.requestWriteAccess,
    requestContact: webApp.requestContact,
    invokeCustomMethod: webApp.invokeCustomMethod,
    invokeCustomMethodAsync: webApp.invokeCustomMethodAsync,
  };
};

// Инициализируем Telegram WebApp
export const initTelegramWebApp = () => {
  if (!isTelegramWebApp()) {
    return false;
  }
  
  const webApp = window.Telegram?.WebApp;
  if (!webApp) {
    return false;
  }
  
  webApp.ready();
  webApp.expand();
  
  // Применяем тему
  applyTelegramTheme();
  
  // Устанавливаем слушатель событий для изменения темы
  setupThemeListener();
  
  return true;
};

// Показываем уведомление с помощью toast
export const showTelegramAlert = (message, type = 'default') => {
  // Определяем тип уведомления по содержимому сообщения
  if (message.includes('✅') || message.includes('успешно')) {
    toast.success(message.replace(/✅\s*/g, ''));
  } else if (message.includes('❌') || message.includes('ошибка') || message.includes('Ошибка')) {
    toast.error(message.replace(/❌\s*/g, ''));
  } else if (message.includes('🔄') || message.includes('ожидайте') || message.includes('загрузка')) {
    toast.loading(message.replace(/[🔄📊🔍]\s*/g, ''));
  } else if (type === 'success') {
    toast.success(message);
  } else if (type === 'error') {
    toast.error(message);
  } else if (type === 'loading') {
    toast.loading(message);
  } else {
    toast(message);
  }
};

// Показываем подтверждение с помощью toast (для обратной совместимости)
export const showTelegramConfirm = (message, callback) => {
  // Для confirm используем toast с вопросом
  toast((t) => (
    <div className="space-y-3">
      <p className="text-sm">{message}</p>
      <div className="flex gap-2 justify-end">
        <button
          onClick={() => {
            toast.dismiss(t.id);
            callback(false);
          }}
          className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 rounded transition-colors"
        >
          Отмена
        </button>
        <button
          onClick={() => {
            toast.dismiss(t.id);
            callback(true);
          }}
          className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
        >
          Подтвердить
        </button>
      </div>
    </div>
  ), {
    duration: Infinity,
    style: {
      background: 'var(--tg-theme-secondary-bg-color)',
      color: 'var(--tg-theme-text-color)',
      border: '1px solid var(--tg-theme-hint-color)',
      borderRadius: '12px',
      fontSize: '14px',
      maxWidth: '350px',
    }
  });
};

// Закрываем текущий popup (устаревшая функция, оставлена для совместимости)
export const closeCurrentPopup = () => {
  // Ничего не делаем, так как toast управляются автоматически
}; 